import map from 'lodash/map'
import React, { useEffect, useRef, useState } from 'react'
import { useContext } from '../Context'
import videoThumb from '../../../../assets/images/video-thumbnail.png'
import pdfIcon from '../../../../assets/images/pdf-icon.jpg'
import fileIcon from '../../../../assets/images/file-icon.jpg'
import { Icon, Image, ImageLoadingPlaceholder, Link, LoadingPlaceholder, ShowIf } from '@ztechapps/webui.util'
import { useDeepCompareEffect } from '@ztechapps/common.hook'
import { ProductSize1Image } from '@ztechapps/product.webui'
import { Card, Ratio, Stack } from 'react-bootstrap'
import type { Asset } from '@ztechapps/common.contract'
import { AssetTypeEnum } from '@ztechapps/common.contract'

export const Gallery = () => {
  const { document, loading } = useContext()
  const coverImage = document?.coverImage
  const allAssets = document?.assets ?? ([{ _id: document?._id, url: coverImage, type: 'image' }] as Asset[])
  const [bigAsset, setBigAsset] = React.useState<Asset>()

  useDeepCompareEffect(() => {
    let imageAsset = allAssets?.[0] ?? ({ _id: document?._id, url: coverImage, type: 'image' } as Asset)

    //set the medium size, and large size
    imageAsset = {
      ...imageAsset,
      mediumUrl: imageAsset.mediumUrl || imageAsset.url,
      largeUrl: imageAsset.largeUrl || imageAsset.url,
      thumbUrl: imageAsset.thumbUrl || imageAsset.url,
    }

    setBigAsset(imageAsset)
  }, [allAssets, coverImage])

  const onClickThumb = (image) => (e) => {
    e.preventDefault()
    setBigAsset(image)
  }

  return (
    <LoadingPlaceholder loading={loading} as={ImageLoadingPlaceholder}>
      <Card>
        <Card.Body className="p-0">
          <ShowIf condition={bigAsset?.type === AssetTypeEnum.image}>
            <div>
              <a href={bigAsset?.largeUrl} target={'blank'}>
                <ProductSize1Image
                  url={bigAsset?.mediumUrl}
                  name={document?.name}
                  className="img-fluid w-100"
                  showLoading={true}
                />
              </a>
            </div>
          </ShowIf>
          <ShowIf condition={bigAsset?.type === AssetTypeEnum.video}>
            <Ratio aspectRatio="16x9">
              <iframe src={bigAsset?.url} title="Gallery video" />
            </Ratio>
          </ShowIf>
          <ShowIf condition={bigAsset?.type === AssetTypeEnum.document || bigAsset?.type === AssetTypeEnum.raw}>
            <div
              style={{
                position: 'relative',
              }}
            >
              <a href={bigAsset?.url} target={'blank'}>
                <Image
                  url={bigAsset?.type === AssetTypeEnum.document ? pdfIcon : fileIcon}
                  name={document?.name}
                  className="img-fluid w-100"
                />
                <div
                  className={'text-warning'}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textShadow: '2px 0 0 #fff, 0 -2px 0 #fff, 0 2px 0 #fff, -2px 0 0 #fff',
                  }}
                >
                  <h4>Click to view the document</h4>
                </div>
              </a>
            </div>
          </ShowIf>
        </Card.Body>

        <Card.Footer>
          <Stack className="text-center pb-4 ">
            <div className={'fw-bold text-primary'}>{bigAsset?.title}</div>
            <div>
              <small> {bigAsset?.subtitle}</small>
            </div>
          </Stack>

          <ScrollableThumbList
            allAssets={allAssets}
            bigAsset={bigAsset}
            onClickThumb={onClickThumb}
            document={document}
          />
        </Card.Footer>
      </Card>
    </LoadingPlaceholder>
  )
}

interface ScrollableThumbListProps {
  allAssets: Asset[]
  bigAsset?: Asset
  onClickThumb: (img: Asset) => (e: any) => void
  document?: { name?: string }
}

export function ScrollableThumbList({ allAssets, bigAsset, onClickThumb, document }: ScrollableThumbListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const checkScroll = () => {
    const el = scrollRef.current
    if (el) {
      setShowLeft(el.scrollLeft > 0)
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth)
    }
  }

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (el) {
      el.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
    }
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  }, [])

  const scrollBy = (distance: number) => {
    scrollRef.current?.scrollBy({ left: distance, behavior: 'smooth' })
  }

  return (
    <div className="position-relative">
      {showLeft && <ScrollArrow onScroll={scrollBy} direction="left" />}

      {showRight && <ScrollArrow onScroll={scrollBy} direction="right" />}

      <div className="d-flex overflow-auto gap-2 px-5" ref={scrollRef} style={{ maxWidth: '100%' }}>
        {map(allAssets, (image: Asset, index) => {
          let url = image?.thumbUrl
          if (image?.type === AssetTypeEnum.video) url = videoThumb
          else if (image?.type === AssetTypeEnum.document) url = pdfIcon
          else if (image?.type === AssetTypeEnum.raw) url = fileIcon

          const itemStyle: React.CSSProperties = {
            height: 80,
            width: 80,
            // flex: '0 0 auto',
          }

          return (
            <Link
              key={image?._id ?? index}
              onClick={onClickThumb(image)}
              className={`border ${bigAsset?._id === image?._id ? 'border-2 border-primary' : ''}`}
            >
              <img src={url} alt={document?.name} style={itemStyle} />
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export const ScrollArrow = ({
  onScroll,
  direction,
}: {
  direction: 'left' | 'right'
  onScroll: (distance: number) => void
}) => {
  return (
    <div
      className={`position-absolute top-50 ${direction === 'left' ? 'start-0' : 'end-0'} translate-middle-y bg-light  p-2 `}
      style={{ zIndex: 10, cursor: 'pointer' }}
      onClick={() => onScroll(direction === 'left' ? -200 : 200)}
    >
      <Icon name={direction === 'left' ? 'chevron-left' : 'chevron-right'} />
    </div>
  )
}
